import React, { useEffect, useRef } from "react";

export default function HalftoneMarquee({
  text = "ONDE VOCÊ PRECISAR",
  speed = "200s",
  gradTop = "#FF7A00",
  gradBottom = "#E85A00",
  dotSize = 2,
  gap = 8,
  baseDotOpacity = 0.22,
  highlightDotOpacity = 1,
  fontSize = "180px",
  // 👇 novos (e responsivos)
  minHeightMobile = "40vh",
  minHeightDesktop = "27.5vh",
  mobileBreakpoint = 768, // até 768px aplica o mobile
  className = "",
  style = {},
}) {
  const sectionRef = useRef(null);
  const svgRef = useRef(null);
  const t1Ref = useRef(null);
  const t2Ref = useRef(null);
  const patternRef = useRef(null);
  const dotRef = useRef(null);
  const animRef = useRef(null);

  // ID estável pra escopo de CSS
  const uidRef = useRef(null);
  if (!uidRef.current) uidRef.current = `id-${Math.random().toString(36).slice(2)}`;
  const uid = uidRef.current;
  const patternId = `dotPattern-${uid}`;
  const maskId = `textMask-${uid}`;

  useEffect(() => {
    const section = sectionRef.current;
    const svg = svgRef.current;
    if (!section || !svg) return;

    const phrase = (text || "ONDE VOCÊ PRECISAR").toUpperCase();
    const chunk = `${phrase} • `;
    const repeats = 30;
    const longLine = chunk.repeat(repeats);
    if (t1Ref.current && t2Ref.current) {
      t1Ref.current.textContent = longLine;
      t2Ref.current.textContent = longLine;
    }

    const syncPattern = () => {
      const cs = getComputedStyle(section);
      const gapVal = parseFloat(cs.getPropertyValue("--gap")) || 8;
      const dotVal = parseFloat(cs.getPropertyValue("--dot-size")) || 2;

      if (patternRef.current) {
        patternRef.current.setAttribute("width", String(gapVal));
        patternRef.current.setAttribute("height", String(gapVal));
      }
      if (dotRef.current) {
        dotRef.current.setAttribute("cx", String(gapVal / 2));
        dotRef.current.setAttribute("cy", String(gapVal / 2));
        dotRef.current.setAttribute("r", String(dotVal / 2));
      }
    };

    const layoutMarquee = () => {
      if (!t1Ref.current || !t2Ref.current) return;
      const bbox = t1Ref.current.getBBox();
      const textWidth = bbox.width || 0;

      t2Ref.current.setAttribute("x", String(textWidth));

      if (animRef.current) {
        animRef.current.setAttribute("from", "0 0");
        animRef.current.setAttribute("to", `-${textWidth} 0`);
        animRef.current.setAttribute("dur", speed);
        try { animRef.current.beginElement(); } catch { }
      }
    };

    const rerun = () => { syncPattern(); layoutMarquee(); };
    rerun();
    requestAnimationFrame(rerun);
    setTimeout(rerun, 200);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(rerun).catch(() => { });
    }

    const onResize = () => rerun();
    window.addEventListener("resize", onResize);
    window.addEventListener("load", rerun);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", rerun);
    };
  }, [text, speed]);

  const cssVars = {
    "--grad-top": gradTop,
    "--grad-bottom": gradBottom,
    "--dot-size": `${dotSize}px`,
    "--gap": `${gap}px`,
    "--base-dot-opacity": baseDotOpacity,
    "--highlight-dot-opacity": highlightDotOpacity,
    "--font-size": fontSize,
    "--mask-soft-vignette":
      "linear-gradient(130deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0) 85%)",
    // 👇 expõe valores para o CSS
    "--min-h-mobile": minHeightMobile,
    "--min-h-desktop": minHeightDesktop,
    ...style,
  };

  return (
    <section
      ref={sectionRef}
      className={`halftone-hero ${className}`}
      data-uid={uid}
      aria-label="Seção decorativa com padrão de pontos e texto rolante"
      // ⚠️ não definir minHeight inline — o CSS abaixo cuida disso
      style={cssVars}
    >
      <style>{`
        /* Escopo por data-uid pra não vazar */
        .halftone-hero[data-uid="${uid}"] {
          position: relative;
          width: 100%;
          overflow: hidden;
          color: #fff;
          background: transparent;
          isolation: isolate;
          font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
          min-height: var(--min-h-desktop);
        }
        @media (max-width: ${mobileBreakpoint}px) {
          .halftone-hero[data-uid="${uid}"] {
            min-height: var(--min-h-mobile);
          }
        }

        /* malha fraca (apenas pattern) + vinheta via mask */
        .halftone-hero[data-uid="${uid}"] .dots-base {
          position: absolute;
          inset: 0;
          pointer-events: none;
          --dot-color: rgba(255, 255, 255, var(--base-dot-opacity));
          background-image: radial-gradient(
            circle at center,
            var(--dot-color) var(--dot-size),
            transparent calc(var(--dot-size) + 0.1px)
          );
          background-size: var(--gap) var(--gap);
          background-repeat: repeat;
          background-position: 0 0;
          -webkit-mask-image: var(--mask-soft-vignette);
          -webkit-mask-size: 100% 100%;
          -webkit-mask-repeat: no-repeat;
          mask-image: var(--mask-soft-vignette);
          mask-size: 100% 100%;
          mask-repeat: no-repeat;
        }
        .halftone-hero[data-uid="${uid}"] .dots-mask { position: absolute; inset: 0; pointer-events: none; }
        .halftone-hero[data-uid="${uid}"] .dots-mask svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
        .halftone-hero[data-uid="${uid}"] .mask-text {
          font-family: inherit;
          font-weight: 800;
          letter-spacing: 0.04em;
          font-size: var(--font-size);
          dominant-baseline: middle;
        }
      `}</style>

      {/* Camada 1: malha fraca */}
      <div className="dots-base" aria-hidden="true" />

      {/* Camada 2: malha forte dentro do texto (mask SVG) */}
      <div className="dots-mask" aria-hidden="true">
        <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Pontos destacados dentro do texto">
          <defs>
            <pattern id={patternId} ref={patternRef} patternUnits="userSpaceOnUse" width="8" height="8">
              <circle
                ref={dotRef}
                cx="4"
                cy="4"
                r="1"
                fill="white"
                style={{ opacity: "var(--highlight-dot-opacity)" }}
              />
            </pattern>

            <mask id={maskId} maskUnits="userSpaceOnUse">
              <rect x="0" y="0" width="100%" height="100%" fill="black" />
              <g fill="white">
                <text className="mask-text" ref={t1Ref} x="0" y="50%" />
                <text className="mask-text" ref={t2Ref} x="0" y="50%" />
                <animateTransform
                  ref={animRef}
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="0 0"
                  dur={speed}
                  repeatCount="indefinite"
                />
              </g>
            </mask>
          </defs>

          <rect x="0" y="0" width="100%" height="100%" fill={`url(#${patternId})`} mask={`url(#${maskId})`} />
        </svg>
      </div>
    </section>
  );
}
